import { useEffect, useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { StatCard, SectionCard } from "@project/admin/components/module-ui";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  Users,
  Stethoscope,
  Building2,
  Ambulance,
  Activity,
  PhoneCall,
  User,
  Clock,
  HeartPulse,
  RefreshCw,
  CalendarRange,
} from "lucide-react";
import { healthcareDashboardService } from "@project/services/admin/healthcare-dashboard.service";
import type { HealthcareDashboardData } from "@project/types/healthcare-dashboard";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const CHART_COLORS = ["var(--primary)", "#10b981", "#f59e0b", "#6366f1", "#ec4899", "#14b8a6"];

const tooltipStyle = {
  background: "var(--card)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  fontSize: 12,
  boxShadow: "0 8px 24px rgba(0,0,0,.08)",
} as const;


export default function HealthcareDashboard() {
  const [data, setData] = useState<HealthcareDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = () => {
    setLoading(true);
    setError(null);
    healthcareDashboardService
      .fetch()
      .then((res) => {
        if (res) {
          setData(res);
        } else {
          setError("Failed to retrieve dashboard statistics.");
        }
      })
      .catch(() => {
        setError("Error loading healthcare dashboard data.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <AdminShell
        title="Healthcare"
        subtitle="Clinic activity, medical registry & hospital transfers"
      >
        <div className="flex h-[400px] flex-col items-center justify-center gap-3">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground animate-pulse">Loading clinic statistics...</p>
        </div>
      </AdminShell>
    );
  }

  if (error || !data) {
    return (
      <AdminShell
        title="Healthcare"
        subtitle="Clinic activity, medical registry & hospital transfers"
      >
        <div className="flex h-[400px] flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-border p-8 text-center bg-white shadow-sm">
          <div className="rounded-full bg-destructive/10 p-3 text-destructive">
            <Activity className="h-6 w-6" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Unable to load dashboard</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm">
              {error || "An unexpected error occurred while fetching healthcare statistics."}
            </p>
          </div>
          <Button onClick={fetchDashboardData} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Retry Connection
          </Button>
        </div>
      </AdminShell>
    );
  }

  const { summary, medical_staff, hospitals, patients, treatments, referrals } = data;

  // Prepare chart datasets
  const staffChartData = [
    { name: "Doctors", value: summary.total_doctors || 0 },
    { name: "Nurses", value: summary.total_nurses || 0 }
  ].filter(item => item.value > 0);

  const hospitalChartData = (patients.by_hospital || []).map(h => ({
    name: h.hospital || "Unknown",
    count: h.count || 0
  }));

  const bloodGroupChartData = (patients.by_blood_group || []).map(b => ({
    name: b.blood_group || "Unknown",
    count: b.count || 0
  }));

  return (
    <AdminShell
      title="Healthcare Dashboard"
      subtitle="Real-time clinic activity, medical staff registry & partner hospital networks"
    >

      {/* 5-Column Stat Cards Row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <StatCard
          label="Total Patients"
          value={summary.total_patients}
          icon={<Users className="h-5 w-5" />}
          accent="from-primary/95 to-primary/80"
          sub="Admitted patients"
        />
        <StatCard
          label="Medical Staff"
          value={summary.total_medical_staff}
          icon={<HeartPulse className="h-5 w-5" />}
          accent="from-emerald-600 to-emerald-500"
          sub={`${summary.active_medical_staff} active on duty`}
        />
        <StatCard
          label="Partner Hospitals"
          value={summary.total_hospitals}
          icon={<Building2 className="h-5 w-5" />}
          accent="from-amber-600 to-amber-500"
          sub={`${summary.active_hospitals} active units`}
        />
        <StatCard
          label="Treatments Today"
          value={summary.today_treatment_sessions}
          icon={<Stethoscope className="h-5 w-5" />}
          accent="from-purple-600 to-purple-500"
          sub={`${summary.total_treatment_sessions} total log records`}
        />
        <StatCard
          label="Active Referrals"
          value={summary.total_referrals}
          icon={<Ambulance className="h-5 w-5" />}
          accent="from-rose-600 to-rose-500"
          sub={`${summary.today_referrals} transferred today`}
        />
      </div>

      {/* Visual Analytics Charts */}
      <div className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {/* Patients by Hospital */}
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="mb-3">
            <h3 className="text-base font-bold text-foreground">Patients by Hospital</h3>
            <p className="text-xs text-muted-foreground">Distribution across partner units</p>
          </div>
          <div className="h-[260px] w-full">
            {hospitalChartData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                No patient hospital data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hospitalChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(0,0,0,0.02)" }} />
                  <Bar dataKey="count" fill="var(--primary)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Medical Staff Composition */}
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="mb-3">
            <h3 className="text-base font-bold text-foreground">Medical Staff Composition</h3>
            <p className="text-xs text-muted-foreground">Active Doctors vs Nursing Staff</p>
          </div>
          <div className="h-[260px] w-full flex items-center justify-center">
            {staffChartData.length === 0 ? (
              <p className="text-xs text-muted-foreground">No staff data available</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={staffChartData}
                    cx="50%"
                    cy="45%"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {staffChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Patients by Blood Group */}
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm md:col-span-2 xl:col-span-1">
          <div className="mb-3">
            <h3 className="text-base font-bold text-foreground">Patient Blood Groups</h3>
            <p className="text-xs text-muted-foreground">Breakdown of admitted seniors</p>
          </div>
          <div className="h-[260px] w-full">
            {bloodGroupChartData.length === 0 ? (
              <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                No patient blood group data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bloodGroupChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(0,0,0,0.02)" }} />
                  <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">

        {/* Left Span - Recent Activity Tables */}
        <div className="space-y-6 lg:col-span-2">
          {/* Recent Patients */}
          <SectionCard
            title="Recent Admitted Patients"
            subtitle="Latest registered patient records"
          >
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border/80 text-muted-foreground text-xs font-semibold uppercase">
                    <th className="py-2.5">File No</th>
                    <th className="py-2.5">Full Name</th>
                    <th className="py-2.5">Age/Sex</th>
                    <th className="py-2.5">Room</th>
                    <th className="py-2.5">Diagnosis</th>
                    <th className="py-2.5 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {patients.recent.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-muted-foreground">
                        No recent patient records found.
                      </td>
                    </tr>
                  ) : (
                    patients.recent.map((p) => (
                      <tr key={p.id} className="hover:bg-muted/10 transition-colors">
                        <td className="py-3 font-semibold text-xs text-primary">{p.file_number}</td>
                        <td className="py-3 font-medium text-foreground">{p.full_name}</td>
                        <td className="py-3 text-xs">
                          {p.age} Yrs / {p.gender}
                        </td>
                        <td className="py-3 text-xs text-muted-foreground">{p.room_number || "N/A"}</td>
                        <td className="py-3 text-xs max-w-[150px] truncate" title={p.diagnosis}>
                          {p.diagnosis || "No diagnosis"}
                        </td>
                        <td className="py-3 text-right">
                          <Badge
                            variant={p.patient_status === "Admitted" ? "default" : "secondary"}
                            className="text-[10px]"
                          >
                            {p.patient_status}
                          </Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </SectionCard>

          {/* Recent Treatments */}
          <SectionCard
            title="Recent Treatments Log"
            subtitle="Latest recorded care & consultation entries"
          >
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border/80 text-muted-foreground text-xs font-semibold uppercase">
                    <th className="py-2.5">Date</th>
                    <th className="py-2.5">Patient</th>
                    <th className="py-2.5">Care Section</th>
                    <th className="py-2.5">Doctor Assigned</th>
                    <th className="py-2.5 text-right">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {treatments.recent.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-muted-foreground">
                        No recent treatments logged.
                      </td>
                    </tr>
                  ) : (
                    treatments.recent.map((t) => (
                      <tr key={t.id} className="hover:bg-muted/10 transition-colors">
                        <td className="py-3 text-xs text-muted-foreground">{t.date}</td>
                        <td className="py-3 font-medium text-foreground">{t.patient_name}</td>
                        <td className="py-3 text-xs">{t.care_section || "General"}</td>
                        <td className="py-3 text-xs font-semibold text-primary">{t.doctor_assigned || "N/A"}</td>
                        <td className="py-3 text-right text-xs max-w-[200px] truncate" title={t.treatment_details}>
                          {t.treatment_details}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </SectionCard>

          {/* Hospital Transfers */}
          <SectionCard
            title="Hospital Referrals & Transfers"
            subtitle="Seniors transferred to external partner hospitals"
          >
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border/80 text-muted-foreground text-xs font-semibold uppercase">
                    <th className="py-2.5">Date</th>
                    <th className="py-2.5">Patient</th>
                    <th className="py-2.5">Partner Hospital</th>
                    <th className="py-2.5">Reason</th>
                    <th className="py-2.5 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {referrals.recent.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-muted-foreground">
                        No hospital transfer records found.
                      </td>
                    </tr>
                  ) : (
                    referrals.recent.map((r) => (
                      <tr key={r.id} className="hover:bg-muted/10 transition-colors">
                        <td className="py-3 text-xs text-muted-foreground">{r.date}</td>
                        <td className="py-3 font-medium text-foreground">{r.patient_name}</td>
                        <td className="py-3 text-xs font-semibold text-primary">{r.partner_hospital}</td>
                        <td className="py-3 text-xs truncate max-w-[150px]" title={r.referral_reason}>
                          {r.referral_reason}
                        </td>
                        <td className="py-3 text-right">
                          <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">
                            {r.referral_status}
                          </Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </div>

        {/* Right Span - Registry Lists */}
        <div className="space-y-6">
          {/* Medical Staff */}
          <SectionCard
            title="On-Duty Medical Staff"
            subtitle={`${medical_staff.active} active doctors & nurses`}
          >
            <div className="space-y-4">
              {medical_staff.recent.length === 0 ? (
                <p className="text-sm text-center py-6 text-muted-foreground">No staff members registered.</p>
              ) : (
                medical_staff.recent.map((s) => (
                  <div
                    key={s.id}
                    className={`flex flex-col gap-1.5 p-3 rounded-lg border text-sm transition-all hover:shadow-sm bg-white ${
                      s.is_highlighted ? "border-primary/40 bg-primary/5" : "border-border/60"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-foreground flex items-center gap-1.5">
                        <User className="h-4 w-4 text-primary shrink-0" />
                        {s.name}
                      </span>
                      <Badge variant="secondary" className="capitalize text-[10px]">
                        {s.staff_type}
                      </Badge>
                    </div>
                    {s.designation && <p className="text-xs text-muted-foreground font-medium">{s.designation}</p>}
                    {s.qualification && (
                      <p className="text-xs text-muted-foreground/80 font-semibold">
                        Qual: <span className="font-normal">{s.qualification}</span>
                      </p>
                    )}
                    {s.timings && (
                      <p className="text-[11px] text-primary/80 bg-primary/5 rounded px-2 py-1 mt-1 font-medium flex items-center gap-1.5">
                        <Clock className="h-3 w-3 shrink-0" />
                        {s.timings}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </SectionCard>

          {/* Partner Hospitals */}
          <SectionCard
            title="Partner Hospital Units"
            subtitle={`${hospitals.active} active healthcare networks`}
          >
            <div className="space-y-4">
              {hospitals.recent.length === 0 ? (
                <p className="text-sm text-center py-6 text-muted-foreground">No partner hospitals configured.</p>
              ) : (
                hospitals.recent.map((h) => (
                  <div
                    key={h.id}
                    className="flex flex-col gap-1.5 p-3.5 rounded-lg border border-border/80 text-sm bg-white hover:shadow-sm transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-foreground flex items-center gap-1.5">
                        <Building2 className="h-4 w-4 text-amber-600 shrink-0" />
                        {h.hospital_name}
                      </span>
                      <Badge variant={h.is_active ? "outline" : "secondary"} className="text-[9px] border-emerald-200 text-emerald-700 bg-emerald-50">
                        {h.is_active ? "Active Partner" : "Inactive"}
                      </Badge>
                    </div>
                    {h.subtitle && <p className="text-xs text-muted-foreground leading-relaxed">{h.subtitle}</p>}
                    
                    {/* Emergency Contact */}
                    {(h.emergency_contact_name || h.emergency_contact_number) && (
                      <div className="mt-2 text-xs border-t border-dashed border-border/60 pt-2 flex flex-col gap-1">
                        <p className="font-medium text-foreground flex items-center gap-1.5">
                          <PhoneCall className="h-3.5 w-3.5 text-rose-500 shrink-0" />
                          Emergency Helpline:
                        </p>
                        <p className="text-muted-foreground pl-5 font-semibold text-[11px]">
                          {h.emergency_contact_name || "Helpline"}: <span className="text-rose-600 font-bold">{h.emergency_contact_number}</span>
                        </p>
                      </div>
                    )}

                    {/* Timings */}
                    {h.hospital_timings && (
                      <p className="text-[11px] text-muted-foreground/80 pl-5 flex items-center gap-1">
                        <CalendarRange className="h-3 w-3 shrink-0" />
                        Timings: {h.hospital_timings}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </SectionCard>
        </div>
      </div>
    </AdminShell>
  );
}
